/**
 * Stanford SSO auth context (P30 / F admin gating).
 *
 * Wraps Google Identity Services so the SPA can:
 *   1. Render a Sign-In button that issues a Google ID token (hd=stanford.edu).
 *   2. Hold the token in memory + localStorage for `apiFetch` to use as Bearer.
 *      localStorage (not sessionStorage) so opening a new browser tab does
 *      NOT prompt for login again — it shares the session across all tabs.
 *      A `storage` event listener keeps tabs in sync on sign-out too.
 *   3. Call /api/v1/me to confirm the user is on the portal allowlist.
 *   4. Expose `me` (email, role, is_admin) + sign-out everywhere via `useAuth()`.
 *
 * The same `OIDC_GOOGLE_CLIENT_IDS[0]` env var the backend uses is read here
 * via `VITE_GOOGLE_CLIENT_ID` (Vite's build-time env). The hosted-domain
 * restriction is enforced both client-side (in the GIS init) AND server-side
 * (`googleClaimMapper` rejects any `hd != stanford.edu`).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

interface MeResponse {
  email: string;
  display_name: string | null;
  role: 'admin' | 'viewer' | 'bootstrap' | null;
  is_member: boolean;
  is_admin: boolean;
}

interface AuthContextValue {
  /** Raw Google ID token; null until signed in. */
  readonly token: string | null;
  /** Decoded /api/v1/me response; null until verified. */
  readonly me: MeResponse | null;
  /** Loading initial sign-in / verification. */
  readonly isLoading: boolean;
  /** Last error from sign-in or /me. */
  readonly error: string | null;
  /** Whether Google Identity Services has loaded + initialize() has run. */
  readonly gisReady: boolean;
  /** Trigger Google sign-out + clear local state. */
  readonly signOut: () => void;
  /** Imperatively re-render the GIS button into a target element. */
  readonly renderButton: (el: HTMLElement) => void;
  /**
   * Best-effort silent re-auth via Google's One Tap prompt. Resolves
   * `true` if a new credential arrives (replaces the current token via
   * `handleCredential`). Resolves `false` if the prompt was suppressed,
   * dismissed, or didn't yield a credential within the timeout. Used by
   * `SessionExpiryBanner` to extend the session before the warn window
   * fires so an actively-typing user doesn't see a countdown.
   */
  readonly refreshSession: () => Promise<boolean>;
}

const TOKEN_KEY = 'sumcp_google_id_token';
const AuthContext = createContext<AuthContextValue | null>(null);

const GOOGLE_CLIENT_ID =
  (import.meta.env?.VITE_GOOGLE_CLIENT_ID as string | undefined) ??
  '274222214444-8olbaa0i4lt5tm2b4imkvp9p1u9ottkg.apps.googleusercontent.com';
// Empty string or `*` disables the hd restriction (V1 demo with personal
// Google accounts). Default keeps the Stanford Workspace lock-in.
const HOSTED_DOMAIN: string = (() => {
  const raw = import.meta.env?.VITE_GOOGLE_HOSTED_DOMAIN as string | undefined;
  if (raw === undefined) return 'stanford.edu';
  if (raw === '*' || raw === '') return '';
  return raw;
})();
const CONTROL_PLANE_BASE = (import.meta.env?.VITE_CONTROL_PLANE_URL as string | undefined) ?? '';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(opts: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
            hd?: string;
            ux_mode?: 'popup' | 'redirect';
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            /** Opt into FedCM (federated credential management) for the
             *  One Tap prompt. Required once Google removes the legacy
             *  iframe-prompt path. */
            use_fedcm_for_prompt?: boolean;
          }): void;
          renderButton(
            el: HTMLElement,
            opts: {
              type?: 'standard' | 'icon';
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with';
              logo_alignment?: 'left' | 'center';
              width?: number;
            },
          ): void;
          disableAutoSelect(): void;
          revoke(email: string, cb: () => void): void;
          // Re-prompt the One Tap UI. Returns synchronously; the
          // credential arrives via the `callback` registered in init.
          prompt(
            notif?: (n: {
              isNotDisplayed?: () => boolean;
              isSkippedMoment?: () => boolean;
              isDismissedMoment?: () => boolean;
              getNotDisplayedReason?: () => string;
              getDismissedReason?: () => string;
            }) => void,
          ): void;
        };
      };
    };
  }
}

let gsiScriptPromise: Promise<void> | null = null;
function loadGsiScript(): Promise<void> {
  if (gsiScriptPromise) return gsiScriptPromise;
  gsiScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) return resolve();
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
  return gsiScriptPromise;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  // Clear the legacy `sumcp_session` cookie that older builds wrote
  // (it held a Google ID token; once that token expires every API
  // call it's auto-attached to gets rejected 401 by auth-fastify
  // before our cookie hook can run). Safe to call always — it only
  // clears that one specific cookie, the new portal session cookie
  // (`sumcp_portal_session`) is HttpOnly and JS can't touch it.
  if (typeof document !== 'undefined') {
    document.cookie = 'sumcp_session=; Path=/; Max-Age=0; SameSite=Lax';
  }
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState<MeResponse | null>(null);
  // Initial mount always tries /api/v1/me once — the cookie may already
  // be valid from a prior sign-in even when there's no localStorage
  // token. After that we only verify when `token` changes (i.e. a fresh
  // sign-in flow is in progress).
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [gisReady, setGisReady] = useState(false);
  const initializedRef = useRef(false);
  // Set by `signOut` so the next firing of the verify effect is a no-op
  // — without this, signOut → setToken(null) re-triggers the effect and
  // we hit /api/v1/me with no auth, logging a noisy 401 to the console
  // even though we *just* explicitly cleared the session ourselves.
  const skipNextVerifyRef = useRef(false);

  // Single source of truth for "is this caller authenticated?" — hits
  // /api/v1/me and either populates `me` or signals a clean unauth
  // state. Called from the mount effect AND from `handleCredential`
  // after the cookie exchange (instead of relying on a state change to
  // re-trigger an effect, which is unreliable when the state is already
  // at its target value).
  const verifyMe = useCallback(async (bearerToken?: string | null): Promise<void> => {
    setIsLoading(true);
    const headers: Record<string, string> = { accept: 'application/json' };
    if (bearerToken) headers.authorization = `Bearer ${bearerToken}`;
    try {
      const r = await fetch(`${CONTROL_PLANE_BASE}/api/v1/me`, {
        headers,
        credentials: 'include',
      });
      if (r.status === 401) {
        // No valid session — treat as silent unauth, render the login
        // button. Don't surface a user-facing error.
        setMe(null);
        setError(null);
        return;
      }
      if (r.status === 403) {
        const body = (await r.json().catch(() => ({}))) as { error_description?: string };
        setMe(null);
        setError(body.error_description ?? 'forbidden');
        return;
      }
      if (!r.ok) {
        setMe(null);
        setError(`Could not reach the gateway (status ${r.status})`);
        return;
      }
      const m = (await r.json()) as MeResponse;
      setMe(m);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCredential = useCallback(
    async (idToken: string) => {
      // Exchange the Google ID token (1h TTL, can't be extended) for a
      // server-issued portal session cookie (sliding 48h, HttpOnly).
      // After the exchange we drop the Google ID token entirely —
      // keeping it in localStorage would poison subsequent API calls
      // with an expired Bearer that auth-fastify rejects 401 BEFORE the
      // cookie hook gets a chance to run.
      setIsLoading(true);
      setError(null);
      try {
        const r = await fetch(`${CONTROL_PLANE_BASE}/api/v1/portal/session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ id_token: idToken }),
        });
        if (!r.ok) {
          const body = (await r.json().catch(() => ({}))) as { error_description?: string };
          const msg = body.error_description ?? `session exchange failed (${r.status})`;
          // FedCM's auto-reauthn occasionally hands back a cached Google
          // ID token whose `exp` is already in the past. The server then
          // rejects it with "Token expired" / "invalid_id_token". This
          // is recoverable — clear the GIS cache and re-prompt for a
          // fresh token. Don't show the user a scary error for what's
          // an auth-flow hiccup.
          const looksLikeStaleToken =
            r.status === 401 || /token expired|exp.* in the past|invalid_id_token/i.test(msg);
          if (looksLikeStaleToken) {
            try {
              window.google?.accounts?.id?.disableAutoSelect?.();
            } catch {
              /* GIS not available */
            }
            // Drop local state cleanly and present a friendly prompt-
            // again message rather than the raw upstream string.
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setMe(null);
            setError('Sign-in expired — please click Continue with Google again.');
            setIsLoading(false);
            return;
          }
          throw new Error(msg);
        }
        // Cookie is now set. Call /api/v1/me explicitly to populate
        // `me` and clear `isLoading` — we can't rely on the verify
        // effect because token-state may already be null (cold visit).
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        await verifyMe(null);
      } catch (err) {
        setError((err as Error).message);
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setIsLoading(false);
      }
    },
    [verifyMe],
  );

  // Initialize GIS once when the provider mounts.
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    loadGsiScript()
      .then(() => {
        if (!window.google?.accounts?.id) throw new Error('GIS not available after load');
        const initOpts: Parameters<typeof window.google.accounts.id.initialize>[0] = {
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => {
            void handleCredential(resp.credential);
          },
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: false,
          // FedCM is becoming mandatory — opt in now so One Tap keeps
          // working when Google removes the legacy iframe-prompt path.
          // Per https://developers.google.com/identity/gsi/web/guides/fedcm-migration
          use_fedcm_for_prompt: true,
        };
        if (HOSTED_DOMAIN) initOpts.hd = HOSTED_DOMAIN;
        window.google.accounts.id.initialize(initOpts);
        setGisReady(true);
      })
      .catch((e) => setError((e as Error).message));
  }, [handleCredential]);

  // Cross-tab sync: when another tab writes/clears the token in localStorage,
  // mirror that into this tab's React state so sign-in / sign-out propagates.
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== TOKEN_KEY) return;
      const next = e.newValue;
      setToken(next);
      if (!next) {
        setMe(null);
        setError(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Verify session on mount and whenever `token` changes. The cookie
  // is the primary auth path; Bearer is a legacy fallback for the
  // brief sign-in window before the cookie exchange completes.
  useEffect(() => {
    if (skipNextVerifyRef.current) {
      // We just signed out explicitly — no need to ask the server, we
      // know we're not authenticated. Avoids a useless 401 in the log.
      skipNextVerifyRef.current = false;
      setIsLoading(false);
      return;
    }
    void verifyMe(token);
  }, [token, verifyMe]);

  // Best-effort silent re-auth. We listen for the next localStorage write
  // to the token key — `handleCredential` fires it on success. Times out
  // after 5s to avoid hanging the banner / activity loop forever when GIS
  // suppresses the prompt (cooldown, blocked third-party cookies, etc.).
  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (!window.google?.accounts?.id) return false;
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const before = localStorage.getItem(TOKEN_KEY);
      const onStorage = (e: StorageEvent): void => {
        if (e.key !== TOKEN_KEY || !e.newValue || e.newValue === before) return;
        cleanup();
        resolve(true);
      };
      // GIS doesn't fire StorageEvents for the same tab, so also poll.
      const poll = window.setInterval(() => {
        const cur = localStorage.getItem(TOKEN_KEY);
        if (cur && cur !== before) {
          cleanup();
          resolve(true);
        }
      }, 250);
      const timer = window.setTimeout(() => {
        cleanup();
        resolve(false);
      }, 5000);
      const cleanup = (): void => {
        if (settled) return;
        settled = true;
        window.removeEventListener('storage', onStorage);
        window.clearInterval(poll);
        window.clearTimeout(timer);
      };
      window.addEventListener('storage', onStorage);
      try {
        const gis = window.google?.accounts?.id;
        if (!gis) {
          cleanup();
          resolve(false);
          return;
        }
        gis.prompt((n) => {
          // If GIS won't show the prompt (cookies blocked, cooldown, etc.)
          // bail early so the caller doesn't wait the full 5s.
          if (n && (n.isNotDisplayed?.() || n.isSkippedMoment?.() || n.isDismissedMoment?.())) {
            cleanup();
            resolve(false);
          }
        });
      } catch {
        cleanup();
        resolve(false);
      }
    });
  }, []);

  const signOut = useCallback(() => {
    // Best-effort revoke of the server-side session row. If this
    // fails (network blip, server restart) the cookie still gets
    // cleared client-side and the next request will fail at the
    // server — same end state for the user.
    fetch(`${CONTROL_PLANE_BASE}/api/v1/portal/session`, {
      method: 'DELETE',
      credentials: 'include',
    }).catch(() => undefined);
    {
      localStorage.removeItem(TOKEN_KEY);
      document.cookie = 'sumcp_session=; Path=/; Max-Age=0';
    }
    // Tell GIS to forget the last-used Google account so the next
    // "Continue with Google" click prompts the picker fresh, rather
    // than silently re-issuing a (possibly stale) cached credential.
    try {
      window.google?.accounts?.id?.disableAutoSelect?.();
    } catch {
      /* GIS not initialized yet — fine, no cache to clear */
    }
    skipNextVerifyRef.current = true;
    setToken(null);
    setMe(null);
    setError(null);
    setIsLoading(false);
    if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.disableAutoSelect();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const renderButton = useCallback(
    (el: HTMLElement) => {
      // Only safe AFTER initialize() has run (gisReady true). Callers should
      // gate on `gisReady` before calling, but we guard here too so we
      // don't blow up with the misleading "Failed to render button before
      // calling initialize()" GIS log if the race happens anyway.
      if (!gisReady || !window.google?.accounts?.id) return;
      window.google.accounts.id.renderButton(el, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        logo_alignment: 'left',
        width: 320,
      });
    },
    [gisReady],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ token, me, isLoading, error, gisReady, signOut, renderButton, refreshSession }),
    [token, me, isLoading, error, gisReady, signOut, renderButton, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Read the bearer token from outside React (used by `apiFetch`). */
export function getStoredAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
